import React, { useMemo } from 'react';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import { Button, DropdownMenu, Icon } from '@kontentino/ui';
import { faCog } from '@fortawesome/pro-regular-svg-icons/faCog';
import { faCreditCard } from '@fortawesome/pro-regular-svg-icons/faCreditCard';
import { faFolder } from '@fortawesome/pro-regular-svg-icons/faFolder';
import { faGear } from '@fortawesome/pro-regular-svg-icons/faGear';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons/faShareNodes';
import { faUserGroup } from '@fortawesome/pro-regular-svg-icons/faUserGroup';
import { faUserPlus } from '@fortawesome/pro-regular-svg-icons/faUserPlus';
import Link from 'components/shared/Link';
import VerticalNavigationMenuOption from '../VerticalNavigationMenuOption';
import VerticalNavigationDropdownOption from './UserActionDropdownOption';
import { VerticalMenuItemWrapper } from '../VerticalMenuItemWrapper';
import { Module } from 'config';
import routes from 'constants/routes';
import { useTranslation } from 'react-i18next';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { composeRoute, modalRoutes } from 'constants/routes';
import LanguageSubmenu from './LanguageSubmenu';
import { ActionButton } from 'app/components/actions/ActionButton';
import { useActions } from 'app/hooks/useActions';

type Props = {};

const Settings: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { isTrial } = useSubscriptionInfo();
  const { profileActions } = useActions('profile-selector');

  return useMemo(() => {
    return (
      <VerticalMenuItemWrapper>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger data-name="navbar_settings">
            <VerticalNavigationMenuOption
              icon={<Icon size={18} icon={faCog} />}
              label={t('navigation.userSubActions.settings')}
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            side="right"
            sideOffset={-10}
            className="tw-z-10 tw-min-w-[240px]"
          >
            <UserPermissionGate
              scopes={[UserPermission.PAGE_CREATE, UserPermission.USER_CREATE]}
              exact={false}
            >
              <>
                <div className="tw-m-2 tw-flex tw-flex-col tw-gap-2">
                  <ActionButton
                    action={profileActions.add}
                    data-name="settings_add-profile_button"
                    data-cy="settings_add-profile_button"
                    variant="primary"
                    size="medium"
                    className="tw-w-full"
                  >
                    {t('addProfile')}
                  </ActionButton>
                  <UserPermissionGate scopes={UserPermission.USER_CREATE}>
                    <Link
                      to={{
                        hash: modalRoutes.INVITE_USER,
                      }}
                      data-name="settings_invite-user_button"
                      data-cy="settings_invite-user_button"
                      className="tw-block"
                    >
                      <Button
                        iconBefore={<Icon icon={faUserPlus} />}
                        variant="tertiary"
                        size="medium"
                        className="tw-w-full"
                      >
                        <span>{t('inviteUser')}</span>
                      </Button>
                    </Link>
                  </UserPermissionGate>
                </div>
                <DropdownMenu.Separator />
              </>
            </UserPermissionGate>

            <UserPermissionGate scopes={UserPermission.ACCESS_PROFILES}>
              <Link to={Module.OldApp.URL.SETTINGS.PROFILES}>
                <DropdownMenu.Item data-name="navbar_settings_profiles">
                  <VerticalNavigationDropdownOption
                    icon={<Icon icon={faShareNodes} size="sm" />}
                    label={t('navigation.userSubActions.profiles')}
                  />
                </DropdownMenu.Item>
              </Link>
            </UserPermissionGate>
            <UserPermissionGate scopes={UserPermission.ACCESS_PROFILE_GROUPS}>
              <Link to={Module.OldApp.URL.SETTINGS.PROFILE_GROUPS}>
                <DropdownMenu.Item data-name="navbar_settings_profile-groups">
                  <VerticalNavigationDropdownOption
                    icon={<Icon icon={faFolder} size="sm" />}
                    label={t('navigation.userSubActions.profileGroups')}
                  />
                </DropdownMenu.Item>
              </Link>
            </UserPermissionGate>
            <UserPermissionGate scopes={UserPermission.ACCESS_USERS}>
              <Link to={{ pathname: routes.SETTINGS.USERS }}>
                <DropdownMenu.Item data-name="navbar_settings_users">
                  <VerticalNavigationDropdownOption
                    icon={<Icon icon={faUserGroup} size="sm" />}
                    label={t('navigation.userSubActions.users')}
                  />
                </DropdownMenu.Item>
              </Link>
            </UserPermissionGate>
            <UserPermissionGate
              scopes={
                isTrial
                  ? UserPermission.ACCESS_GLOBAL_PAGES
                  : UserPermission.ACCESS_USERS
              }
            >
              <Link to={Module.OldApp.URL.SETTINGS.KONTENT_BASE}>
                <DropdownMenu.Item data-name="navbar_settings_kontentbase">
                  <VerticalNavigationDropdownOption
                    icon={<Icon icon={faGlobe} size="sm" />}
                    label={t('navigation.userSubActions.kontentBase')}
                  />
                </DropdownMenu.Item>
              </Link>
            </UserPermissionGate>
            <UserPermissionGate scopes={UserPermission.ACCESS_BILLING}>
              <>
                <DropdownMenu.Separator />
                <Link
                  to={composeRoute('settings.routes.billing.path')}
                  isRouterLink
                >
                  <DropdownMenu.Item data-name="navbar_settings_billing">
                    <VerticalNavigationDropdownOption
                      icon={<Icon icon={faCreditCard} size="sm" />}
                      label={t('navigation.userSubActions.billingDashboard')}
                    />
                  </DropdownMenu.Item>
                </Link>
                <DropdownMenu.Separator />
              </>
            </UserPermissionGate>

            <LanguageSubmenu />

            <UserPermissionGate scopes={UserPermission.ACCESS_COMPANY_SETTINGS}>
              <>
                <DropdownMenu.Separator />
                <Link to={Module.OldApp.URL.SETTINGS.COMPANY_SETTINGS}>
                  <DropdownMenu.Item data-name="navbar_settings_company">
                    <VerticalNavigationDropdownOption
                      icon={<Icon icon={faGear} size="sm" />}
                      label={t('navigation.userSubActions.general')}
                    />
                  </DropdownMenu.Item>
                </Link>
              </>
            </UserPermissionGate>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </VerticalMenuItemWrapper>
    );
  }, [isTrial, profileActions.add, t]);
};

export default Settings;
